import { AISearchResultResponse } from '@/schema'

/**
 * Represents a version group with its matches
 */
export interface VersionGroup {
  versionId: string
  versionNumber: number
  highlights: string[]
  matchCount: number
}

/**
 * Represents a document (file) group with its versions
 */
export interface DocumentGroup {
  documentId: string
  displayName: string
  documentType: string
  totalMatches: number
  totalVersions: number
  versionCount: number
  versions: VersionGroup[]
  keywords: string[]
}

/**
 * A single highlight with its position in the flattened list and relative version context.
 * Used for navigation (e.g. next/previous) and any logic that needs a global highlight index.
 */
export interface HighlightWithVersionIndex {
  /** Document (file) display name */
  displayName: string
  /** Document (file) id */
  documentId: string
  /** Version id */
  versionId: string
  /** Numeric version (e.g. 1, 2) for display */
  versionNumber: number
  /** Index of this highlight within the version's highlights array (0-based) */
  highlightIndex: number
  /** Index in the flattened list of all highlights across all documents/versions (0-based) */
  globalIndex: number
  /** Highlight snippet HTML */
  highlight: string
  /** Search keywords for this document */
  keywords: string[]
}

/**
 * Extract version number from version name
 * Assumes format like "Version 1", "Version 2", etc.
 */
const extractVersionNumber = (versionName: string | undefined): number => {
  if (!versionName) return 0

  // Try to match "Version X" pattern
  const versionPattern = /Version\s+(\d+)/i
  const match = versionPattern.exec(versionName)
  if (match?.[1]) {
    return Number.parseInt(match[1], 10)
  }

  // Fallback: try to extract any number from the string
  const numberPattern = /(\d+)/
  const numberMatch = numberPattern.exec(versionName)
  if (numberMatch?.[1]) {
    return Number.parseInt(numberMatch[1], 10)
  }

  return 0
}

/**
 * Groups search results by document and version
 * Sorts documents using compareDocs and versions from newest to oldest
 */
export const groupAndSortSearchResults = (
  results: AISearchResultResponse[]
): DocumentGroup[] => {
  // Step 1: Group by document_id
  const documentMap = new Map<string, AISearchResultResponse[]>()

  results.forEach((result) => {
    const existing = documentMap.get(result.document_id) || []
    documentMap.set(result.document_id, [...existing, result])
  })

  // Step 2: Transform to DocumentGroup structure
  const documentGroups: DocumentGroup[] = Array.from(documentMap.entries()).map(
    ([documentId, docResults]) => {
      // Group by version within this document
      const versionMap = new Map<string, AISearchResultResponse[]>()

      docResults.forEach((result) => {
        const existing = versionMap.get(result.document_version_id) || []
        versionMap.set(result.document_version_id, [...existing, result])
      })

      // Create version groups
      const versions: VersionGroup[] = Array.from(versionMap.entries()).map(
        ([versionId, versionResults]) => {
          // Collect all highlights from all results for this version
          const allHighlights = versionResults.flatMap(
            (r) => r.content_highlights || []
          )

          return {
            versionId,
            versionNumber: extractVersionNumber(
              versionResults[0]?.version_number.toString()
            ),
            highlights: allHighlights,
            matchCount: allHighlights.length,
          }
        }
      )

      // Sort versions: newest first (higher version number first)
      versions.sort((a, b) => b.versionNumber - a.versionNumber)

      // Calculate total matches across all versions
      const totalMatches = versions.reduce(
        (sum, version) => sum + version.matchCount,
        0
      )

      return {
        documentId,
        displayName: docResults[0]?.title || '',
        documentType: docResults[0]?.tfl_type || '',
        totalMatches,
        totalVersions: docResults[0]?.total_versions || 0,
        versionCount: versions.length,
        versions,
        keywords: docResults[0]?.keywords || [],
      }
    }
  )

  return documentGroups
}

/**
 * Flattens all highlights from grouped results into a single list, preserving
 * document/version context and both per-version and global indices.
 *
 * @param groupedResults - Result of groupAndSortSearchResults
 * @returns Array of highlights with documentId, versionId, versionNumber,
 *          highlightIndex (within version), globalIndex (in this list), highlight text, keywords
 */
export const getAllHighlightsWithVersions = (
  groupedResults: DocumentGroup[]
): HighlightWithVersionIndex[] => {
  const out: HighlightWithVersionIndex[] = []
  let globalIndex = 0

  for (const doc of groupedResults) {
    for (const version of doc.versions) {
      version.highlights.forEach((highlight, highlightIndex) => {
        out.push({
          displayName: doc.displayName,
          documentId: doc.documentId,
          versionId: version.versionId,
          versionNumber: version.versionNumber,
          highlightIndex,
          globalIndex,
          highlight,
          keywords: doc.keywords,
        })
        globalIndex += 1
      })
    }
  }

  return out
}
